/**
 * The station screens as a feature (plan §6 M2-18, order `FEATURE_ORDER.station`). Career mode only.
 *
 * - `init`: reads the `m2` service (the career feature provides it earlier, order 150), mounts `#ui-root` and
 *   renders `<App store={m2.ui} />`. The App shows nothing while the store's screen is `none` (in space).
 * - Keyboard: a capture-phase `keydown` listener (arrows/WASD, Enter/Space, Backspace, Q/E, L); gamepad: `frame`
 *   polls the pad (D-pad or left stick, A, B, LB/RB, Y). Both go through `./input.ts`, active only while a screen
 *   is up and the pause menu is closed.
 * - In the Reach (`ctx.reach` non-null, M3 plan R22f): the screen also shows the Reach's station services (Charts,
 *   Parts, Home) as tabs after M2's, through `createReachStationTabs`. CL-6: the tab promotion signals read the
 *   strategic relations mirror (bounties, the player corporation) and the Wire section's unread badge, read-only;
 *   Esc (the loop's Esc stack) closes the station's More drawer before it opens the pause menu.
 */
import { createElement } from 'react'
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { createReachStationTabs } from '../../bridge/m2/stationTabs'
import { FEATURE_ORDER } from '../../app/feature'
import { logger } from '../../core/logger'
import { loadSettingsConfig } from '../../settings/settings'
import type { M2Runtime } from '../../space/contracts'
import { App } from '../../ui/App'
import { allDashSections, type DashContext } from '../../ui/dashboard/registry'
import { createNavBus, createUiPad } from '../../ui/nav'
import { mountUiRoot, type UiRoot } from '../../ui/root'
import { createStationInput, setStationPaused, stationFrame, stationKeyDown, type StationInput } from './input'

let input: StationInput | null = null
let root: UiRoot | null = null
let offs: (() => void)[] = []

function onKeyDown(e: KeyboardEvent): void {
  if (input !== null) stationKeyDown(input, e)
}

/** CL-6: the dashboard's Wire section id and the context its unread badge is read in (docked, career in the Reach). */
const WIRE_SECTION = 'intel.wire'
const WIRE_BADGE_CTX: DashContext = Object.freeze({ mode: 'career', docked: true, career: true })

/** CL-6: notable Wire items not yet read (the Wire section's badge; 0 without it). */
function wireUnread(): number {
  return allDashSections().find((s) => s.id === WIRE_SECTION)?.badge?.(WIRE_BADGE_CTX) ?? 0
}

/** CL-5: the Market's goods rows run (the career service's strategic market and `goods.tradeEnabled`), so Parts folds in. */
function goodsMarketRuns(m2: M2Runtime & Partial<{ goodsMarketRuns: () => boolean }>): boolean {
  return m2.data.career.goods.tradeEnabled && m2.goodsMarketRuns !== undefined && m2.goodsMarketRuns()
}

function hasStore(m2: M2Runtime | undefined): m2 is M2Runtime {
  return m2 !== undefined && typeof m2.ui === 'object' && m2.ui !== null && typeof m2.ui.getState === 'function'
}

/** Per-frame pad polling (allocation-free, D-014). */
function frameStation(ctx: GameContext, f: FrameInfo): void {
  if (input !== null) stationFrame(input, ctx.input.pad, f.dt)
}

const feature: GameFeature = {
  name: 'station',
  order: FEATURE_ORDER.station,
  init(ctx: GameContext): void {
    if (ctx.mode !== 'career') return
    const m2 = ctx.services.maybe('m2')
    if (!hasStore(m2)) {
      logger.warn('Station UI: no m2 service with a UI store; the station screens are off')
      return
    }
    const el = document.getElementById('ui-root')
    if (el === null) {
      logger.error('Station UI: #ui-root is missing from index.html')
      return
    }
    const bus = createNavBus()
    const s = createStationInput(m2.ui, bus, createUiPad(loadSettingsConfig().menuNav, ctx.data.bindings.gamepad.buttons.pause))
    input = s
    root = mountUiRoot(el)
    root.render(
      createElement(App, {
        store: m2.ui,
        data: m2.data,
        nav: bus,
        external:
          ctx.reach === null
            ? undefined
            : createReachStationTabs({
                core: ctx.reach,
                services: ctx.reach.services,
                goodsMarketRuns: () => goodsMarketRuns(m2),
                relations: () => ctx.services.maybe('strategic')?.relations ?? null,
                wireUnread,
              }),
        escape: ctx.ui.escape,
        requestPointerLock: (): void => {
          if (navigator.webdriver) return // automated browsers never lock the user's cursor (see IS_TEST in app/boot.ts)
          void Promise.resolve(ctx.canvas.requestPointerLock()).catch((err: unknown) => logger.warn('Pointer lock refused at launch', err))
        },
      }),
    )
    window.addEventListener('keydown', onKeyDown, { capture: true })
    offs = [ctx.bus.on('pause', () => setStationPaused(s, true)), ctx.bus.on('resume', () => setStationPaused(s, false))]
  },
  frame(ctx: GameContext, f: FrameInfo): void {
    frameStation(ctx, f)
  },
  dispose(): void {
    window.removeEventListener('keydown', onKeyDown, { capture: true })
    for (const off of offs) off()
    offs = []
    root?.unmount()
    root = null
    input = null
  },
}

export default feature
